// @flow strict
import * as React from 'react';
import IntlContext from './context.js';
import translate, { type Translations, type Values } from './translate.js';
import type { Language, IntlContextType } from './context.js';

type Props = {
  children: React.Node,
  language: Language,
  translations: Translations,
};

type IntlResponse = {
  language: Language,
  translations: Translations,
};

const hasOwn = (obj: Object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

const fetchTranslations = async (
  language: Language,
  keys: string[],
): Promise<Translations> => {
  const params = new URLSearchParams();
  if (keys.length) {
    params.set('keys', keys.join(','));
  }
  const url = params.toString() ? `/intl/${language}?${params.toString()}` : `/intl/${language}`;
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) {
    return {};
  }
  const data: IntlResponse = await res.json();
  return data && data.translations ? data.translations : {};
};

const IntlProvider = ({ children, language, translations: initialTranslations }: Props) => {
  const [translations, setTranslations] = React.useState<Translations>(initialTranslations);
  const pendingKeysRef = React.useRef<Set<string>>(new Set());
  const requestedKeysRef = React.useRef<Set<string>>(new Set(Object.keys(initialTranslations)));
  const inFlightRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    setTranslations(initialTranslations);
    pendingKeysRef.current = new Set();
    requestedKeysRef.current = new Set(Object.keys(initialTranslations));
    inFlightRef.current = false;
  }, [language, initialTranslations]);

  const translateWithTracking = React.useCallback(
    (key: string, values?: Values) => {
      if (!hasOwn(translations, key)) {
        if (!requestedKeysRef.current.has(key)) {
          requestedKeysRef.current.add(key);
          pendingKeysRef.current.add(key);
        }
        return key;
      }
      return translate(translations, key, values);
    },
    [translations],
  );

  React.useEffect(() => {
    if (inFlightRef.current || pendingKeysRef.current.size === 0) {
      return;
    }
    const keys = Array.from(pendingKeysRef.current);
    pendingKeysRef.current.clear();
    inFlightRef.current = true;
    fetchTranslations(language, keys)
      .then(fetched => {
        if (Object.keys(fetched).length) {
          setTranslations(prev => ({ ...prev, ...fetched }));
        }
      })
      .catch(() => {
        keys.forEach(key => requestedKeysRef.current.delete(key));
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  });

  const value = React.useMemo<IntlContextType>(
    () => ({
      language,
      translations,
      translate: translateWithTracking,
    }),
    [language, translations, translateWithTracking],
  );

  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
};

export default IntlProvider;
